<template>
  <div class="m-3" style="height: 90vh">
    <ReportsTable ref="rtable"
                  :settings="{hideTable: true, hideExport: true, title: t('formsOfPayments'), appendQueryParams: {'version': '2'}}"
                  :columns="columnDefs"
                  @onDataLoaded="onDataLoaded"
                  endpoint="reports/accounting/forms-of-payments">
      <template #filter>
        <el-select
            filterable
            clearable
            class="col-span-12 md:col-span-3"
            value-key="name"
            :placeholder="t('selectCustomer')"
            v-model="selectedCustomer"
            @change="onSelectedCustomerChanged"
            remote
            :remote-method="searchCustomer"
        >
          <el-option
              v-for="option in customers"
              :key="option.id"
              :label="option.name"
              :value="option"
          ></el-option>
        </el-select>
        <el-select
            filterable
            clearable
            class="col-span-12 md:col-span-3"
            value-key="fullName"
            :placeholder="t('selectEmployee')"
            v-model="selectedEmployee"
            @change="onSelectedEmployeeChanged"
            remote
            :remote-method="searchEmployees"
        >
          <el-option
              v-for="option in employees"
              :key="option.id"
              :label="option.fullName"
              :value="option"
          ></el-option>
        </el-select>
      </template>
      <template #cards="slotProps">
        <div class="w-full text-xl font-bold mt-2">
          {{ t('formOfPayments') }}
        </div>
        <div class="grid grid-cols-3 gap-4">
          <el-card shadow="never">
            <div class="flex flex-col items-center">
              <div class="text-lg text-gray-500">{{ t('totalIQD') }}</div>
              <div class="text-2xl pt-2">{{
                  formatMoney(slotProps.data?.totalIQD || 0, $ctx.currency)
                }}
              </div>
            </div>
          </el-card>

          <el-card shadow="never">
            <div class="flex flex-col items-center">
              <div class="text-lg text-gray-500">{{ t('totalEPayments') }}</div>
              <div class="text-2xl pt-2">{{
                  formatMoney(slotProps.data?.totalEPayments || 0, $ctx.currency)
                }}
              </div>
            </div>
          </el-card>

          <el-card shadow="never">
            <div class="flex flex-col items-center">
              <div class="text-lg text-gray-500">{{ t('totalUSD') }}</div>
              <div class="text-2xl pt-2">{{
                  formatMoney(slotProps.data?.totalUSD || 0, 'USD')
                }}
              </div>
            </div>
          </el-card>

        </div>
      </template>
      <template #chart>
        <div class="grid grid-cols-2 gap-2">
          <div class="col-span-12 md:col-span-2 chart-card"
               style="height: 400px">
            <v-chart :option="option" autoresize/>
          </div>
        </div>
      </template>
    </ReportsTable>
  </div>
</template>

<script setup lang="ts">
import {onBeforeMount, provide, ref} from 'vue'
import {useI18n} from 'vue-i18n'
import {ColDef} from 'ag-grid-community'
import {useRoute, useRouter} from 'vue-router'
import {useHTTP} from '@/plugins/http'
import {useAlertModal} from '@/plugins/alert-modal/alert-modal'
import {formatMoney} from '@/utils/money'
import ReportsTable from '@/views/reports/v2/table/ReportsTable.vue'
import User from '@/models/user/User'
import {use} from 'echarts/core'
import {CanvasRenderer} from 'echarts/renderers'
import {BarChart, PieChart} from 'echarts/charts'
import {
  DataZoomComponent,
  GridComponent,
  LegendComponent,
  TitleComponent,
  ToolboxComponent,
  TooltipComponent
} from 'echarts/components'
import VChart, {THEME_KEY} from 'vue-echarts'
import Customer from "@/models/customer/Customer";
import {i18nOrderMessages} from "@/models/sales/Order";


// i18n
const {t} = useI18n({
  messages: {
    en: {
      formsOfPayments: 'Forms of Payments',
      formOfPayments: 'Forms of Payments',
      selectEmployee: "Select Cashier",
      selectCustomer: "Select Customer",
      totalIQD: 'Total IQD',
      totalEPayments: 'Total E-payments',
      totalUSD: 'Total USD'
    },
    ar: {
      formsOfPayments: 'العملات المستلمة',
      formOfPayments: 'العملات المستلمة',
      selectEmployee: "اختر الموظف",
      selectCustomer: "اختر الزبون",
      totalIQD: 'اجمالي د.ع',
      totalEPayments: 'اجمالي مدفوع الكترونيا',
      totalUSD: 'اجمالي الدولار'
    }
  }
})

// Data
const rtable = ref<any>()
const columnDefs = ref<ColDef[]>([])

const http = useHTTP()
const route = useRoute()
const router = useRouter()
const alertModal = useAlertModal()


use([
  CanvasRenderer,
  PieChart,
  TitleComponent,
  TooltipComponent,
  LegendComponent,
  BarChart,
  GridComponent,
  ToolboxComponent,
  DataZoomComponent
])

provide(THEME_KEY, 'light')
const option = ref({
  title: {},
  grid: {
    top: 80,
  },
  toolbox: {
    feature: {
      saveAsImage: {}
    }
  },
  tooltip: {
    trigger: 'item',
    formatter: function(params) {
      // If this is the USD segment (which has originalValue property)
      if (params.data.originalValue !== undefined) {
        // Format the original USD value
        return `${params.name}: $${params.data.originalValue.toLocaleString()}`;
      }
      // For other segments, use default formatting
      return `${params.name}: IQD ${params.value.toLocaleString()}`;
    }
  },
  legend: {
    top: 30,
    orient: 'vertical',
    left: 'left',
    formatter: (data: any, x: any) => {
      return `${data}`
    }
  },
  series: [
    {
      type: 'pie',
      radius: '55%',
      center: ['50%', '60%'],
      data: [] as any[]
    }
  ]
})


const employees = ref<User[] | any>();
const selectedEmployee = ref<any>()


const customers = ref<Customer[] | any>();
const selectedCustomer = ref<any>()

onBeforeMount(async () => {
  columnDefs.value = []
})


async function onSelectedEmployeeChanged(event: any) {
  rtable.value.payload.filter = selectedEmployee.value ? selectedEmployee.value.id : null;
  rtable.value.navigate(rtable.value.payload)
}

async function searchEmployees(query: string) {
  if (!query) {
    query = ''
  }
  try {
    const url = `${User.ENDPOINT}?q=${query}`
    const employeesRes = await http.get<User[]>(url)
    employees.value = employeesRes.map((employee) => User.from(employee))
  } catch (error) {
    employees.value = []
  }
}


async function onSelectedCustomerChanged(event: any) {
  rtable.value.payload.customer_id = selectedCustomer.value ?
      selectedCustomer.value.id : null;
  rtable.value.navigate(rtable.value.payload)
}

async function searchCustomer(query: string) {
  if (!query) {
    query = ''
  }
  try {
    const url = `${Customer.ENDPOINT}?q=${query}`
    const customersRes = await http.get<Customer[]>(url)
    customers.value = customersRes.map((customer) => Customer.from(customer))
  } catch (error) {
    customers.value = []
  }
}


function onDataLoaded(data: any) {
  console.log(data)
  const chartData: any = []
  chartData.push({
    value: data.totalIQD,
    name: t('totalIQD')
  });

  chartData.push({
    value: data.totalEPayments,
    name: t('totalEPayments')
  });

  // For USD, we show the label as USD but use the IQD equivalent value (USD*1500) for sizing
  chartData.push({
    value: data.totalUSD/(Math.pow(10, 2)) * 1500, // Multiply by 1500 to show the equivalent IQD value
    name: t('totalUSD'),
    // Store the original value for tooltip display
    originalValue: data.totalUSD/(Math.pow(10, 2))
  });

  option.value.series[0].data = chartData
}
</script>
